import { useEffect, useRef, useState } from 'react';
import { FiArrowUpRight, FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import { FaCalendarAlt, FaChalkboardTeacher, FaEnvelope, FaFolderOpen, FaHandshake, FaLock, FaNewspaper, FaUser } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { ExternalLink, MenuButton, Nav, NavBtnLink, NavLink, NavMenu, ResourceButton, ResourceGroup, ResourceMenu } from './NavbarElements';

export default function NavBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const { pathname } = useLocation();
  const nav = useRef(null);
  const menuButton = useRef(null);
  const resourceButton = useRef(null);
  const closeMenu = () => { setIsOpen(false); setResourcesOpen(false); };
  const isHome = pathname === '/' || pathname === '/home';
  const isResource = ['/presskit', '/media-kit'].includes(pathname);

  useEffect(() => { setIsOpen(false); setResourcesOpen(false); }, [pathname]);
  useEffect(() => {
    const outside = event => { if (!nav.current?.contains(event.target)) closeMenu(); };
    const media = window.matchMedia?.('(max-width: 1000px)');
    const resize = () => {
      closeMenu();
      if (!media.matches && document.activeElement === menuButton.current) nav.current?.querySelector('a')?.focus();
    };
    document.addEventListener('pointerdown', outside);
    media?.addEventListener('change', resize);
    return () => { document.removeEventListener('pointerdown', outside); media?.removeEventListener('change', resize); };
  }, []);

  return <Nav ref={nav} aria-label="Main navigation"
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) closeMenu(); }}
    onKeyDown={event => {
      if (event.key !== 'Escape' || (!isOpen && !resourcesOpen)) return;
      event.preventDefault();
      if (isOpen) menuButton.current?.focus(); else resourceButton.current?.focus();
      closeMenu();
    }}>
    <NavBtnLink to="/home" onClick={closeMenu} aria-label="Ken & Bryn home" aria-current={isHome ? 'page' : undefined}>
      <img src="/logo-transparent.png" alt="" aria-hidden="true" />
    </NavBtnLink>
    <MenuButton ref={menuButton} type="button" aria-label="Toggle navigation menu" aria-expanded={isOpen} aria-controls="main-navigation-menu" onClick={() => { setIsOpen(open => !open); setResourcesOpen(false); }}>
      {isOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
    </MenuButton>
    <NavMenu id="main-navigation-menu" $isOpen={isOpen}>
      <div className="nav-primary">
        <NavLink to="/lessons" onClick={closeMenu}><FaChalkboardTeacher aria-hidden="true" />Lessons</NavLink>
        <NavLink to="/events" onClick={closeMenu}><FaCalendarAlt aria-hidden="true" />Events</NavLink>
        <NavLink to="/about" onClick={closeMenu}><FaUser aria-hidden="true" />About</NavLink>
        <NavLink to="/contact" onClick={closeMenu}><FaEnvelope aria-hidden="true" />Contact</NavLink>
      </div>
      <ResourceGroup>
        <ResourceButton ref={resourceButton} type="button" className={isResource ? 'active' : undefined} aria-expanded={resourcesOpen} aria-controls="navigation-resources" onClick={() => setResourcesOpen(open => !open)}>
          <FaFolderOpen aria-hidden="true" />Resources<FiChevronDown aria-hidden="true" />
        </ResourceButton>
        <p className="nav-resource-heading">Resources</p>
        <ResourceMenu id="navigation-resources" $isOpen={resourcesOpen}>
          <NavLink to="/presskit" onClick={closeMenu}><span><FaNewspaper aria-hidden="true" />Press Kit</span><small>For event organizers & press</small></NavLink>
          <NavLink to="/media-kit" onClick={closeMenu}><span><FaHandshake aria-hidden="true" />Media Kit</span><small>For brands & partnerships</small></NavLink>
        </ResourceMenu>
      </ResourceGroup>
      <ExternalLink href="https://members.kenandbryn.dance" target="_blank" rel="noopener noreferrer" onClick={closeMenu} aria-describedby="members-link-note">
        <FaLock aria-hidden="true" />Members<FiArrowUpRight aria-hidden="true" />
      </ExternalLink>
      <span id="members-link-note" className="nav-sr-only">Opens in a new tab</span>
    </NavMenu>
  </Nav>;
}
